'use client';

import * as React from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { getCountryCallingCode } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';

type Variant = 'primary' | 'subtle';

/**
 * 'Sign in with SRG+' button → modal that mirrors SRG+ web login UX.
 *
 * Smart input: detects whether the user is typing an email or a phone.
 * - First char is a letter → email mode (plain text input)
 * - First char is digit/space/+ → phone mode (PhoneInput with country flag
 *   + dial-code dropdown, formats to E.164)
 * - Empty → 'unset' mode with placeholder 'Email or Phone'
 *
 * On submit forwards to /auth/login?connection={email|sms}&login_hint=<id>,
 * so Auth0 skips its identifier-picker page and sends the code straight away.
 */

type Mode = 'unset' | 'email' | 'phone';

export function SignInButton({
  variant = 'primary',
  returnTo,
  label = 'Sign in with SRG+',
}: {
  variant?: Variant;
  returnTo?: string;
  label?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<Mode>('unset');
  const [emailValue, setEmailValue] = React.useState('');
  const [phoneValue, setPhoneValue] = React.useState<string | undefined>(undefined);
  // Default phone country derived synchronously on first render from
  // navigator.language ('en-US' → US, 'ru-RU' → RU). Synchronous matters —
  // PhoneInput reads defaultCountry only at mount, so a useEffect-based
  // update wouldn't propagate.
  const [defaultCountry] = React.useState<string>(() => {
    if (typeof navigator === 'undefined') return 'US';
    const lang =
      navigator.language || (navigator.languages && navigator.languages[0]) || '';
    const m = lang.match(/-([A-Z]{2})$/);
    return m ? m[1] : 'US';
  });

  const reset = () => {
    setMode('unset');
    setEmailValue('');
    setPhoneValue(undefined);
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  // Handle the initial 'unset' input — switch to email/phone based on what
  // was typed.
  const onUnsetInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (!v) return;
    const first = v[0];
    if (/[a-zA-Z@]/.test(first)) {
      setMode('email');
      setEmailValue(v);
    } else if (/[\d+]/.test(first)) {
      setMode('phone');
      // If user typed "+", it's already E.164 — pass through.
      // If they typed bare digits, PhoneInput would parse them as international
      // (so "9" becomes +95 Myanmar). Prefix the user's default country code
      // so digits stay national.
      if (first === '+') {
        setPhoneValue(v);
      } else {
        try {
          const callingCode = getCountryCallingCode(defaultCountry as CountryCode);
          const digits = v.replace(/\D/g, '');
          setPhoneValue(`+${callingCode}${digits}`);
        } catch {
          setPhoneValue(undefined);
        }
      }
    }
  };

  const canSubmit = React.useMemo(() => {
    if (mode === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.trim());
    if (mode === 'phone') return phoneValue ? isValidPhoneNumber(phoneValue) : false;
    return false;
  }, [mode, emailValue, phoneValue]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const params = new URLSearchParams();
    if (mode === 'email') {
      params.set('connection', 'email');
      params.set('login_hint', emailValue.trim());
    } else if (mode === 'phone' && phoneValue) {
      params.set('connection', 'sms');
      params.set('login_hint', phoneValue);
    }
    if (returnTo) params.set('returnTo', returnTo);

    window.location.href = `/auth/login?${params.toString()}`;
  };

  const buttonStyle: React.CSSProperties =
    variant === 'primary'
      ? {
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          fontSize: 13,
          background: '#fff',
          color: '#000',
          fontWeight: 600,
          border: 0,
          cursor: 'pointer',
        }
      : {
          textDecoration: 'none',
          whiteSpace: 'nowrap',
          fontSize: 12,
          background: 'rgba(204, 48, 48, 0.5)',
          color: '#fff',
          border: '1px solid rgba(255, 122, 122, 0.5)',
          padding: '4px 12px',
          cursor: 'pointer',
        };

  return (
    <>
      <button type="button" className="lk-button" onClick={() => setOpen(true)} style={buttonStyle}>
        {label}
      </button>

      {open && (
        <div
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1a1a22',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 12,
              padding: 28,
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  marginBottom: 12,
                  fontFamily: '"SF Pro Display", system-ui, sans-serif',
                }}
              >
                SRG<span style={{ color: '#4080ff' }}>+</span>
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>
                Let&apos;s get started
              </h2>
              <p style={{ fontSize: 13, opacity: 0.6, margin: 0 }}>
                Please confirm your phone or email to continue.
              </p>
            </div>

            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {mode === 'unset' && (
                <input
                  type="text"
                  placeholder="Email or Phone"
                  autoFocus
                  inputMode="email"
                  onChange={onUnsetInput}
                  style={inputStyle}
                />
              )}

              {mode === 'email' && (
                <div>
                  <input
                    type="email"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    placeholder="you@example.com"
                    autoFocus
                    inputMode="email"
                    autoComplete="email"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setMode('unset');
                      setEmailValue('');
                    }}
                    style={modeSwitchStyle}
                  >
                    ↺ Use phone instead
                  </button>
                </div>
              )}

              {mode === 'phone' && (
                <div className="aipod-phone-input-wrap">
                  <PhoneInput
                    international
                    defaultCountry={defaultCountry as never}
                    value={phoneValue}
                    onChange={setPhoneValue}
                    autoFocus
                    placeholder="Phone number"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setMode('unset');
                      setPhoneValue(undefined);
                    }}
                    style={modeSwitchStyle}
                  >
                    ↺ Use email instead
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  padding: '14px 16px',
                  fontSize: 15,
                  fontWeight: 600,
                  background: canSubmit ? '#4080ff' : 'rgba(64, 128, 255, 0.4)',
                  color: '#fff',
                  border: 0,
                  borderRadius: 8,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                Continue
              </button>
            </form>

            <p
              style={{
                fontSize: 11,
                opacity: 0.4,
                marginTop: 16,
                marginBottom: 0,
                textAlign: 'center',
              }}
            >
              By clicking Continue you agree to receive authentication codes from SRG+. Data rates
              may apply.
            </p>

            <button
              type="button"
              onClick={close}
              style={{
                width: '100%',
                padding: '8px',
                fontSize: 13,
                background: 'transparent',
                color: 'rgba(255,255,255,0.5)',
                border: 0,
                cursor: 'pointer',
                marginTop: 8,
              }}
            >
              Cancel
            </button>
          </div>

          <style jsx global>{`
            .aipod-phone-input-wrap .PhoneInput {
              background: rgba(255, 255, 255, 0.06);
              border: 1px solid rgba(255, 255, 255, 0.15);
              border-radius: 8px;
              padding: 6px 12px;
            }
            .aipod-phone-input-wrap .PhoneInputCountry {
              margin-right: 12px;
              padding-right: 12px;
              border-right: 1px solid rgba(255, 255, 255, 0.1);
            }
            .aipod-phone-input-wrap .PhoneInputCountrySelect {
              opacity: 0;
              position: absolute;
              cursor: pointer;
            }
            .aipod-phone-input-wrap .PhoneInputCountryIcon--border {
              box-shadow: none;
            }
            .aipod-phone-input-wrap input.PhoneInputInput {
              background: transparent;
              border: 0;
              outline: none;
              color: #fff;
              font-size: 15px;
              padding: 8px 0;
              width: 100%;
            }
          `}</style>
        </div>
      )}
    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 16px',
  fontSize: 15,
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: 8,
  color: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const modeSwitchStyle: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  color: 'rgba(64, 128, 255, 0.9)',
  fontSize: 12,
  cursor: 'pointer',
  marginTop: 6,
  padding: '2px 4px',
};
